/**
 * Fiscal years.
 *
 * The district fiscal year runs July 1 – June 30 and is named by its *ending*
 * calendar year: school year 2026-27 is fiscal year 2027 (REQUIREMENTS.md
 * §5.2, confirmed with Matt during Phase 1 planning).
 *
 * Budget codes reset cleanly at each July 1 with no carryover (§11); a code's
 * `fiscalYear` therefore scopes essentially every query in the app.
 */

/** Month index (0-based) in which the fiscal year rolls over. July. */
const ROLLOVER_MONTH = 6

/**
 * The fiscal year containing `date`.
 *
 * Uses local calendar fields deliberately: "which fiscal year is it" is a
 * question about the user's own calendar, and July 1 in UTC can be June 30
 * locally in US timezones.
 */
export function fiscalYearOf(date: Date): number {
  return date.getMonth() >= ROLLOVER_MONTH ? date.getFullYear() + 1 : date.getFullYear()
}

/** The fiscal year we're in right now. */
export function currentFiscalYear(now: Date = new Date()): number {
  return fiscalYearOf(now)
}

/** First instant of a fiscal year — July 1 of the preceding calendar year. */
export function fiscalYearStart(fiscalYear: number): Date {
  return new Date(fiscalYear - 1, ROLLOVER_MONTH, 1, 0, 0, 0, 0)
}

/** Last instant of a fiscal year — end of June 30. */
export function fiscalYearEnd(fiscalYear: number): Date {
  return new Date(fiscalYear, ROLLOVER_MONTH - 1, 30, 23, 59, 59, 999)
}

/** True when `date` falls inside `fiscalYear`. */
export function isInFiscalYear(date: Date, fiscalYear: number): boolean {
  return fiscalYearOf(date) === fiscalYear
}

/** Display label, e.g. `FY2026-27`. */
export function getFiscalYearLabel(fiscalYear: number): string {
  return `FY${fiscalYear - 1}-${String(fiscalYear % 100).padStart(2, '0')}`
}

/**
 * Fiscal years offered in pickers: the current year, the one before it, and
 * the next one. Enough to review last year and set up next year during the
 * July rollover, without an unbounded list.
 */
export function selectableFiscalYears(now: Date = new Date()): number[] {
  const current = currentFiscalYear(now)
  return [current - 1, current, current + 1]
}
